import {
  BookOpen,
  Grid,
  Users,
  Shield,
  Briefcase,
  Layers,
  Award,
  Percent,
  MinusCircle,
  Calendar,
  MapPin,
  Package,
  UserCheck,
  MessageCircle,
  Map,
  Settings,
  Truck,
  FileText,
  List,
  ArrowDownCircle,
  ArrowUpCircle,
  RotateCcw,
  Sliders,
  BarChart2,
  Compass,
  Star,
  Tag,
  Pocket,
  CreditCard,
  CheckSquare,
  TrendingUp,
  Inbox,
  ShoppingBag,
  PieChart,
  GitMerge,
  Search,
  Umbrella,
  Tool,
  DollarSign,
  Radio,
  Droplet,
  AlertTriangle,
} from "react-feather";

// Only screens that actually exist get a nav entry (handoff §9: most modules
// are still empty placeholders in the API) - each item's `permission` is a
// Module.Action code checked against the caller's claims, never a role name.
const navSectionsRaw = [
  {
    label: "Main",
    icon: Grid,
    items: [
      { label: "Dashboard", icon: Grid, path: "/dashboard" },
      // One Stop sits here beside Dashboard rather than under Reports, per
      // direct instruction: it is the everyday "look up everything about one
      // customer/employee/product/vehicle" entry point, not a filed-away
      // report about a single module.
      { label: "One Stop", icon: Search, path: "/reports/one-stop", permission: "Reports.OneStop" },
    ],
  },
  {
    label: "Administration",
    icon: Settings,
    items: [
      {
        label: "Users",
        icon: Users,
        path: "/admin/users",
        permission: "Admin.ManageUsers",
      },
      {
        label: "Roles",
        icon: Shield,
        path: "/admin/roles",
        permission: "Admin.ManageRoles",
      },
      {
        label: "Designations",
        icon: Briefcase,
        path: "/admin/designations",
        permission: "Admin.ManageDesignations",
      },
      {
        label: "Feedback",
        icon: MessageCircle,
        path: "/admin/feedback",
        permission: "Admin.ManageFeedback",
      },
      {
        label: "Settings",
        icon: Settings,
        path: "/admin/settings",
        permission: "Settings.ManageIntegrations",
      },
      {
        // Plain-English permission manual (public/access-guide.html) for whoever sets up users.
        label: "Access Guide",
        icon: BookOpen,
        path: "/admin/access-guide",
        permission: ["Admin.ManageUsers", "Admin.ManageRoles", "Admin.ManageDesignations"],
      },
    ],
  },
  {
    // Live against /api/v1/masters/* (handoff §9, Milestone 3). Each page
    // now has its own {Page}.View/.Create/.Edit permission triad (§13.3) -
    // reading requires .View (checked here, same as Administration above),
    // writes are gated in-page via useMasterPermissions/useCustomerPermissions
    // (.Create/.Edit, plus an all-branch check for the global masters).
    label: "HR Masters",
    icon: Briefcase,
    items: [
      { label: "Customers", icon: MapPin, path: "/masters/customers", permission: ["Customers.View", "Customers.Create", "Customers.Edit"] },
      { label: "Customer Locations", icon: Map, path: "/masters/customers/locations-map", permission: "Customers.ViewLocation" },
      { label: "Customer QR Stickers", icon: Grid, path: "/masters/customers/qr-stickers", permission: ["Customers.View", "Customers.Create", "Customers.Edit"] },
      { label: "Products", icon: Package, path: "/masters/products", permission: ["Products.View", "Products.Create", "Products.Edit"] },
      { label: "Employees", icon: UserCheck, path: "/masters/employees", permission: ["Employees.View", "Employees.Create", "Employees.Edit"] },
      // Vehicles moved to its own "Vehicles" section below (2026-09-19).
      { label: "Departments", icon: Layers, path: "/masters/departments", permission: "Departments.View" },
      { label: "Designations", icon: Award, path: "/masters/designations", permission: ["Designations.View", "Designations.Create", "Designations.Edit"] },
      { label: "Payee (Tax)", icon: Percent, path: "/masters/tax", permission: ["Tax.View", "Tax.Edit"] },
      { label: "Deductions", icon: MinusCircle, path: "/masters/deductions", permission: ["Deductions.View", "Deductions.Create", "Deductions.Edit"] },
      { label: "Holidays", icon: Calendar, path: "/masters/holidays", permission: "Holidays.View" },
      // Moved here from its own "Leave Management" top-level section (2026-09-07),
      // per direct instruction - it sits with the other HR screens rather than
      // standing alone.
      { label: "Mark Leave", icon: Umbrella, path: "/leave", permission: "Leave.View" },
      { label: "Areas", icon: Compass, path: "/masters/areas", permission: ["Area.View", "Area.Create", "Area.Edit"] },
      // React-owned despite feeding Flutter's Sales Enquiry (handoff §9.9) -
      // don't confuse with the global, Flutter-owned Guard Service Type
      // master (§17.9.1); this is the branch-wise top-level service checklist.
      { label: "Service Types", icon: Tag, path: "/masters/service-types", permission: ["Sales.ViewServiceTypes", "Sales.ManageServiceTypes"] },
      // Technical Service Report checklist master (2026-09-02) - lets the
      // business define its own checklist item list (battery volt, PIR
      // tested, etc.) instead of a hardcoded one. Single permission gates
      // view+manage together, no separate ".View" permission - see
      // feedback_no_separate_view_permission_for_masters in project memory.
      { label: "Service Report Checklist", icon: CheckSquare, path: "/technical/checklist-items", permission: "Technical.ManageChecklistItems" },
      // Quotation Prototypes (Phase 1 of src/Quotations) - a reusable named
      // template of line items, same "master data configured once, consumed
      // elsewhere" shape as everything else on this page, so it lives here
      // rather than under Sales Monitoring, per direct instruction.
      { label: "Quotation Prototypes", icon: Layers, path: "/quotations/prototypes", permission: "Quotation.ManagePrototype" },
    ],
  },
  {
    // AMC & Warranty - own top-level module (2026-09-07) with every AMC-related
    // page under it, per direct instruction. Equipment & Coverage is the register
    // of what each customer has plus the warranty on each item; AMC contracts and
    // the service-due list join this section as they land.
    //
    // ONE permission (Amc.ManageEquipment) gates view and manage together - no
    // separate ".View" code, per direct instruction and
    // feedback_no_separate_view_permission_for_masters.
    label: "AMC & Warranty",
    icon: Shield,
    items: [
      // One page: contract, covered equipment and service schedule together. Split across
      // three screens first time round and it was rejected as too many steps to configure.
      // Path kept as /amc/new so existing links and bookmarks still work; the page itself
      // stopped being "new only" on 2026-09-08 and now opens on the whole AMC register,
      // with creating and renewing done from the same screen.
      { label: "AMC Records", icon: Shield, path: "/amc/new", permission: ["Amc.ManageContracts", "Amc.ManageEquipment"] },
    ],
  },
  {
    // Own top-level module, against TexasAlarms.Api's src/Stock module
    // (LPO -> Stock-In/GRN -> Stock-Out -> Return -> Adjustment) - deliberately
    // not nested under HR Masters or Vehicles, per direct instruction.
    label: "Stock Management",
    icon: Package,
    items: [
      { label: "LPOs", icon: FileText, path: "/stock/lpos", permission: ["Stock.ViewLpo", "Stock.CreateLpo", "Stock.EditLpo", "Stock.ApproveLpo", "Stock.CancelLpo", "Stock.PrintLpo", "Stock.CreateStockIn", "Stock.CreateDirectStockIn"] },
      // Pending Vehicle Requests moved to the "Vehicles" section below (2026-09-19).
      { label: "Stock-In (GRN)", icon: ArrowDownCircle, path: "/stock/stock-in", permission: "Stock.ViewStockIn" },
      { label: "Stock-Out", icon: ArrowUpCircle, path: "/stock/stock-out", permission: "Stock.ViewStockOut" },
      { label: "Stock Returns", icon: RotateCcw, path: "/stock/returns", permission: "Stock.ViewReturn" },
      { label: "Stock Adjustments", icon: Sliders, path: "/stock/adjustments", permission: "Stock.ViewAdjustment" },
      { label: "Stock Balances", icon: BarChart2, path: "/stock/balances", permission: "Stock.ViewBalance" },
    ],
  },
  {
    // Everything vehicle-related under ONE menu (2026-09-19), per direct
    // instruction: "make all vehicle related into one main menu in react". The
    // three existing entries were scattered across HR Masters (Vehicles), Stock
    // Management (Pending Vehicle Requests) and Reports (Maintenance Report) -
    // moved here with their paths and permissions exactly as they were, so this
    // is a navigation-only move, not a route change (bookmarks, Quick Menu pins
    // and dashboard links all keep working). Grouped with non-clickable
    // `isGroupLabel` rows, same as Reports.
    label: "Vehicles",
    icon: Truck,
    items: [
      { label: "Fleet", isGroupLabel: true },
      { label: "Vehicles", icon: Truck, path: "/masters/vehicles", permission: ["Vehicles.View", "Vehicles.Create", "Vehicles.Edit"] },
      { label: "Maintenance", isGroupLabel: true },
      { label: "Pending Vehicle Requests", icon: List, path: "/stock/lpos/pending-vehicle-requests", permission: ["Stock.CreateLpo", "Vehicles.ApproveRequest"] },
      // Gated on Vehicles.ViewRequests (governs GET /vehicles/requests itself),
      // not Vehicles.View (which only covers the vehicle master).
      { label: "Maintenance Report", icon: Truck, path: "/reports/vehicles/maintenance", permission: "Vehicles.ViewRequests" },
    ],
  },
  {
    // Fuel is its own main menu (2026-10-02, "make the fuel related sub menus
    // under a main menu called FUEL and seperate it from under vehicles").
    // Navigation-only move: the routes keep their /vehicles/fuel/... paths so
    // bookmarks, Quick Menu pins and dashboard links keep working.
    //
    // Fuel (src/Fuel) is the office half of Fuel Management: prepaid fuel
    // cards, which vehicles/users may use them, the refills that load money
    // onto them, and reading/reviewing the purchases drivers record in the
    // mobile app. Each item's any-of list mirrors the API policy behind that
    // page's own GETs (see useFuelPermissions.js) - spelled out here rather
    // than imported so this file stays plain data like every other section.
    label: "Fuel",
    icon: Droplet,
    items: [
      {
        label: "Fuel Cards", icon: CreditCard, path: "/vehicles/fuel/cards",
        permission: ["Fuel.ManageCards", "Fuel.CreateRefill", "Fuel.ApproveRefill", "Fuel.ViewAllTransactions", "Fuel.ReviewExceptions", "Fuel.ViewReports"],
      },
      // The Vehicle <-> Fuel Card assignment screen (many-to-many), plus each
      // vehicle's fuel type and tank capacity.
      {
        label: "Vehicle Fuel Setup", icon: Sliders, path: "/vehicles/fuel/vehicle-setup",
        permission: ["Fuel.ManageCards", "Fuel.CreateRefill", "Fuel.ApproveRefill", "Fuel.ViewAllTransactions", "Fuel.ReviewExceptions", "Fuel.ViewReports"],
      },
      // Money the office loads onto a card - not fuel purchases.
      { label: "Fuel Card Refills", icon: ArrowDownCircle, path: "/vehicles/fuel/refills", permission: ["Fuel.CreateRefill", "Fuel.ApproveRefill", "Fuel.ViewReports"] },
      // Read-only here; purchases are recorded in the mobile app. A holder of
      // only Fuel.ViewTransactions sees their own rows.
      {
        label: "Fuel Transactions", icon: Droplet, path: "/vehicles/fuel/transactions",
        permission: ["Fuel.ViewTransactions", "Fuel.ViewAllTransactions", "Fuel.ReviewExceptions", "Fuel.ViewReports"],
      },
      { label: "Fuel Exceptions", icon: AlertTriangle, path: "/vehicles/fuel/exceptions", permission: ["Fuel.ReviewExceptions", "Fuel.ViewAllTransactions", "Fuel.ViewReports"] },
      { label: "Fuel Reports", icon: BarChart2, path: "/vehicles/fuel/reports", permission: "Fuel.ViewReports" },
      // One month, a column per day: each vehicle's fuel purchases and card refills.
      { label: "Fuel Monthly Summary", icon: Calendar, path: "/vehicles/fuel/monthly-summary", permission: "Fuel.ViewReports" },
    ],
  },
  {
    // Own top-level module, against TexasAlarms.Api's src/Technical module -
    // React port of the Flutter-only module (2026-09-08), zero backend change.
    //
    // Scope is deliberately the MANAGER's half: approve (push a confirmed
    // order into Technical), assign technicians, hold/resume, complete,
    // verify & close, and read the full detail. Field activities - the QR
    // check-in, the technician's own service report and the product
    // diagnosis - stay in the mobile app; their results are shown here
    // read-only, since verifying them is what this module is for.
    //
    // The Service Report Checklist master is NOT repeated here - it lives
    // with the other configured-once masters under HR Masters.
    label: "Technical",
    icon: Tool,
    items: [
      {
        label: "Technical Work",
        icon: Tool,
        path: "/technical/work",
        // Any of the four view codes: the three granular ones also restrict
        // WHICH rows come back, so an Installations-only viewer sees a
        // shorter list rather than being locked out.
        permission: [
          "Technical.View",
          "Technical.ViewComplaints",
          "Technical.ViewServiceVisits",
          "Technical.ViewInstallations",
        ],
      },
      {
        label: "Pending Assignment",
        icon: Inbox,
        path: "/technical/pending-assignment",
        permission: "Technical.AssignToTechnical",
      },
      {
        label: "Duty List",
        icon: List,
        path: "/technical/duty-list",
        permission: [
          "Technical.View",
          "Technical.ViewComplaints",
          "Technical.ViewServiceVisits",
          "Technical.ViewInstallations",
        ],
      },
    ],
  },
  {
    // Alarm Response (2026-09-17, Batch177): the control room dispatches
    // alarms to patrol cars and acts on what they report back. The car's own
    // steps happen in the mobile app.
    label: "Rapid Response",
    icon: Radio,
    items: [
      { label: "Control Room", icon: Radio, path: "/alarms/board", permission: ["Alarms.Dispatch", "Alarms.View"] },
      { label: "Response Report", icon: FileText, path: "/alarms/report", permission: ["Alarms.Dispatch", "Alarms.View"] },
      { label: "Mobile Units", icon: Truck, path: "/alarms/mobile-units", permission: "Alarms.ManageUnits" },
    ],
  },
  {
    // Own top-level module, against TexasAlarms.Api's src/PettyCash module -
    // React port of the Flutter-only module (2026-08-29). No Settings item
    // here - Self-Pay Ceiling is already editable on the existing
    // Administration > Settings page (Petty Cash group), not duplicated.
    label: "Petty Cash",
    icon: Pocket,
    items: [
      { label: "Fund Balances", icon: Pocket, path: "/pettycash/funds", permission: "PettyCash.ViewBalance" },
      { label: "Vouchers", icon: CreditCard, path: "/pettycash/vouchers", permission: "PettyCash.View" },
      // Batch168 - change handed back against a paid voucher, credited to its fund.
      { label: "Voucher Returns", icon: RotateCcw, path: "/pettycash/returns", permission: ["PettyCash.RecordReturn", "PettyCash.ViewAll"] },
      // Refills left this menu on 2026-09-08 by instruction. They are requested and
      // reviewed from Fund Balances now - the page for the funds they credit - and
      // approvers also still get them in the pending-approvals badge.
      { label: "Reconciliations", icon: CheckSquare, path: "/pettycash/reconciliations", permission: "PettyCash.View" },
      // Back here from Reports on 2026-09-12 by instruction - the ledger is the
      // day-to-day book, not an occasional report.
      { label: "Ledger", icon: List, path: "/pettycash/ledger", permission: "PettyCash.ViewLedger" },
      { label: "Categories", icon: Tag, path: "/pettycash/categories", permission: "PettyCash.View" },
    ],
  },
  {
    // Own top-level module, against TexasAlarms.Api's src/Sales module -
    // React port of the Flutter-only module (2026-09-01). Upcoming
    // Deployments moved to its own Guarding section below (Phase 1,
    // 2026-09-02) - it's gated by the DEPLOYMENT module's own permissions,
    // not Sales.*.
    label: "Sales Monitoring",
    icon: TrendingUp,
    items: [
      { label: "Dashboard", icon: BarChart2, path: "/sales/dashboard", permission: "Sales.ViewReports" },
      // Any-of, not Sales.View alone: someone who can raise, approve or close
      // work here must be able to reach the screen that does it. Gating on
      // .View meant a user with Sales.CreateEnquiry but no Sales.View saw no
      // Sales menu at all - same rule already applied API-side 2026-09-09.
      {
        label: "Enquiries", icon: Inbox, path: "/sales/enquiries",
        permission: ["Sales.View", "Sales.ViewAll", "Sales.CreateEnquiry", "Sales.ApproveEnquiry",
          "Sales.ApproveWithExternalQuotation", "Sales.BookForOthers", "Sales.UploadQuotation"],
      },
      {
        label: "Orders", icon: ShoppingBag, path: "/sales/orders",
        permission: ["Sales.View", "Sales.ViewAll", "Sales.CreateConfirmedOrder", "Sales.CloseOrder",
          "Sales.BookForOthers", "Sales.ManageOperations"],
      },
      // Commission worklist (2026-09-08) - its own screen rather than a filter
      // on Orders, because paying commission is a different job done by a
      // different person, over CLOSED orders only. Gated by the same single
      // permission that guards the write endpoints.
      { label: "Commission", icon: DollarSign, path: "/sales/commissions", permission: "Sales.ManageCommission" },
      // Client Visits (Batch182) - the reporting end of the printed client
      // visit report; the visits themselves are recorded in the field from
      // the Flutter app. Any-of, same rule as Enquiries/Orders above: someone
      // who records visits must be able to look them up here, not only
      // whoever holds the branch-wide view.
      {
        label: "Client Visits", icon: CheckSquare, path: "/sales/client-visits",
        permission: ["Sales.ViewAllClientVisits", "Sales.ViewClientVisits", "Sales.LogClientVisit"],
      },
      // Quotations (Phase 1) - own module/DB/API (src/Quotations). The
      // Prototypes master itself moved to HR Masters (see above); this stays
      // here since day-to-day quotation creation/approval is a Sales
      // Monitoring activity.
      {
        label: "Quotations", icon: FileText, path: "/quotations",
        permission: ["Quotation.View", "Quotation.ViewAll", "Quotation.Create", "Quotation.Edit",
          "Quotation.EditApproved", "Quotation.Approve", "Quotation.Send", "Quotation.Cancel"],
      },
    ],
  },
  {
    // Own top-level module, Phase 1 (2026-09-02) - against TexasAlarms.Api's
    // new src/Guarding module (reuses the existing DEPLOYMENT module's
    // permissions, live-named "Guarding", per direct instruction not to
    // create a second module). Guard Remarks & Grading moved here from HR
    // Masters per direct instruction - it's guard-duty performance history,
    // not a plain master.
    // Collections (2026-09-10) - read-only lookup list. Its own section rather
    // than an item under Sales, because a collection is money received against
    // an existing customer, not part of the sales pipeline. Creating/editing a
    // collection stays Flutter-only, so there is deliberately no "New" entry.
    label: "Collections",
    icon: DollarSign,
    items: [
      { label: "Collections", icon: DollarSign, path: "/collections", permission: "Collections.View" },
      // Batch189 - who owns which customer, and how each collector did against it.
      { label: "Collector Lists", icon: Users, path: "/collections/collector-lists", permission: ["Collections.ManageAllocation", "Collections.ViewPerformance"] },
      { label: "Collector Performance", icon: BarChart2, path: "/collections/collector-performance", permission: "Collections.ViewPerformance" },
    ],
  },
  {
    label: "Guarding",
    icon: Shield,
    items: [
      // React port of the Flutter Attendance Dashboard (2026-09-09) - first in the
      // section because it is the screen a supervisor or duty manager opens
      // repeatedly through a shift, not one they go looking for. Same
      // Attendance.Mark|Attendance.View policy as Guard Lookup below.
      { label: "Attendance Dashboard", icon: UserCheck, path: "/guarding/attendance-dashboard", permission: ["Attendance.Mark", "Attendance.View"] },
      { label: "Pending Deployments", icon: MapPin, path: "/guarding/pending-deployments", permission: "Deployment.View" },
      // React port of the Flutter Attendance module's Guard Lookup screen -
      // "who was guarding this customer, in this date range" - backend
      // policy is Attendance.Mark|Attendance.View, so either permission
      // shows this item (see itemIsVisible in Sidebar.jsx).
      { label: "Guard Lookup", icon: Search, path: "/guarding/guard-lookup", permission: ["Attendance.Mark", "Attendance.View"] },
      // Web version of the WinForms Duty List report (2026-09-14), same policy.
      { label: "Guard Duty List", icon: Grid, path: "/guarding/duty-list", permission: ["Attendance.Mark", "Attendance.View"] },
      { label: "Supervisor Areas", icon: Compass, path: "/guarding/supervisor-areas", permission: "Deployment.ManageSupervisorAreas" },
      // Office Attendance Posts (2026-09-15, Batch173) - the carrier slot each
      // office employee's kiosk punch is credited to. Attendance.View is accepted
      // alongside the new Attendance.ManagePosts so the managers who already own
      // the attendance screens can use it before that permission is seeded.
      { label: "Office Attendance Posts", icon: MapPin, path: "/guarding/office-attendance-posts", permission: ["Attendance.ManagePosts", "Attendance.View"] },
      { label: "Guard Remarks & Grading", icon: Star, path: "/masters/employees/remarks", permission: "Employees.ViewRemarks" },
    ],
  },
  {
    // Own top-level module (2026-09-03) - every historical/analytical report
    // page used to live inside its own module's section (Stock Management's
    // "Movement Report", Petty Cash's "Ledger"/"Summary Report"/"Voucher
    // Register") mixed in among that module's day-to-day transaction
    // screens. Consolidated here per direct instruction, grouped by the
    // module each report belongs to via a non-clickable `isGroupLabel` row
    // (see Sidebar.jsx) rather than a flat list, so this section scales
    // cleanly as more modules grow their own reports. Paths/permissions are
    // unchanged from where each page lived before - this is a navigation-only
    // move, not a route change.
    label: "Reports",
    icon: PieChart,
    items: [
      { label: "Cross-module", isGroupLabel: true },
      // Batch174 - quotation item traced through LPO -> Stock-In -> Stock-Out,
      // with delivery and margin variances. Its own permission because it is
      // the only report exposing per-item cost AND margin.
      { label: "Transaction Reconciliation", icon: GitMerge, path: "/reports/reconciliation", permission: "Reports.TransactionReconciliation" },
      { label: "Stock", isGroupLabel: true },
      // Batch66 - management-only ledger report, dedicated permission
      // (Reports.StockMovement) under the REPORTS module, not Stock.*.
      { label: "Movement Report", icon: FileText, path: "/stock/movement-report", permission: "Reports.StockMovement" },
      { label: "Petty Cash", isGroupLabel: true },
      // Ledger + Voucher Register + Summary on one page (2026-09-15).
      { label: "Overview", icon: Layers, path: "/pettycash/reports/overview", permission: ["PettyCash.ViewLedger", "PettyCash.ViewReports"] },
      { label: "Summary Report", icon: BarChart2, path: "/pettycash/reports/summary", permission: "PettyCash.ViewReports" },
      { label: "Voucher Register", icon: FileText, path: "/pettycash/reports/register", permission: "PettyCash.ViewReports" },
      // The Vehicles group (Maintenance Report) moved to the "Vehicles" section
      // above (2026-09-19).
      // Technician Duty Report (2026-09-11) - the same four view codes as the
      // Technical Work list: the granular ones narrow which job TYPES come
      // back, and a field technician only ever sees their own lines.
      { label: "Technical Work", isGroupLabel: true },
      {
        label: "Technician Duty Report",
        icon: Users,
        path: "/reports/technical/duty",
        permission: ["Technical.View", "Technical.ViewComplaints", "Technical.ViewServiceVisits", "Technical.ViewInstallations"],
      },
    ],
  },
];

// Sidebar order (2026-09-23, by instruction): "keep Main, Administration and HR Masters as it
// is and all other main menus sorted in alphabetic order other than reports" - the three fixed
// sections lead in the order written above, every other section follows A-Z by label, and
// Reports closes the list. New sections written above land in the right place on their own.
const FIXED_FIRST = ["Main", "Administration", "HR Masters"];
const FIXED_LAST = ["Reports"];
export const navSections = [
  ...FIXED_FIRST.map((label) => navSectionsRaw.find((s) => s.label === label)).filter(Boolean),
  ...navSectionsRaw
    .filter((s) => !FIXED_FIRST.includes(s.label) && !FIXED_LAST.includes(s.label))
    .sort((a, b) => a.label.localeCompare(b.label)),
  ...FIXED_LAST.map((label) => navSectionsRaw.find((s) => s.label === label)).filter(Boolean),
];
